
import { computed, defineComponent, onMounted, reactive, toRefs, PropType, watch, ref } from 'vue'
import { useStore } from 'vuex'

import Directr, { Timerange } from '@/services/directr'

import Page from '@/layouts/Page.vue'

import { date, useQuasar } from 'quasar'
import StatusIndicator from '@/components/StatusIndicator.vue'
import ChartSkeleton from '@/components/Chart/ChartSkeleton.vue'
import PageFilter from '@/layouts/PageFilter.vue'
import CountTo from '@/components/CountTo.vue'
import useCategoryFilter from '@/pages/composables/useCategoryFilter'
import { CtCatalogue, CtCategory, CtPacking, CtProduct, CtStockItem } from '@/models/interfaces/content-type'

import LineChart from '@/components/Chart/LineChart.vue'
import DonutChart from '@/components/Chart/DonutChart.vue'
import AreaChart from '@/components/Chart/AreaChart.vue'
import Boxplot from '@/components/Chart/Boxplot.vue'
import BarChart from '@/components/Chart/BarChart.vue'
import HeatmapChart from '@/components/Chart/HeatMapChart.vue'

import { InsightResolution, InsightPoint } from '@/models/interfaces/insight'
import { format } from '@/utils/template'
import { DATE_FORMAT } from '@/constants'
import Charts from './composables'

import * as Mock from './constants'
import { minmax } from './utils'
import { $groupBy, debounce } from '@/utils'
import { EntrySys } from '@Protium/vue-ozone-utils'

import DButton from '@/components/base/DButton.vue'
import { errorToReadableString } from '@/utils/error'
import moment from 'moment'
import { AxisChartOptions, ScaleTypes } from '@carbon/charts/interfaces'


interface InsightsState {
  timerange: Timerange,
  resolution: InsightResolution,
  points?: Array<InsightPoint>,
  services?: Array<Record<string, any>>,
  servicePoints?: Record<string, { id: string, type: string, items: Array<InsightPoint> }>
  catalogues?: Array<{ sys: EntrySys, id: CtCatalogue['id'], name: CtCatalogue['name'] }>
  legend: Array<string>,
  showFilters: boolean
}

const INSIGHT_SERVICES = ['shopify']

export default defineComponent({
  name: 'InsightsOverview',
  components: { Page, LineChart, DonutChart, AreaChart, Boxplot, ChartSkeleton, BarChart, StatusIndicator, PageFilter, HeatmapChart, CountTo, DButton },
  setup (props, { emit }) {
    const $store = useStore()
    const $directr: Directr = $store.getters['Services/Directr']
    const $sys = $store.getters['Services/Sys']
    const selectedItems = ref([])
    const $q = useQuasar()

    const loading = ref(false)

    const defaultChartOptions: AxisChartOptions = {
      // title: 'Orders by day',
      axes: Mock.DEFAULT_AXES,
      height: '300px'
    }

    //to handle the case when all filters are removed and we need to reset
    //the charts to the original data
    watch(selectedItems, (newVal, oldVal) => {
      if (newVal.length) {
        console.log(newVal)
        newVal.forEach((cat, i) => {
          toggleFilter(cat, i)
        })
      }
      else {
        charts.ordersByCatalogueDay = { data: ordersByCatalogueDay.value, options: charts.ordersByCatalogueDay.options }
        charts.productAvgPerOrdersByDay = { data: productAvgPerOrdersByDay.value, options: charts.ordersByCatalogueDay.options }
      }
    })

    const state = reactive<InsightsState>({
      timerange: { start: date.startOfDate(date.subtractFromDate(new Date(), { months: 1 }), 'day').toISOString(), end: date.endOfDate(new Date(), 'day').toISOString() },
      resolution: InsightResolution.Day,
      services: [],
      servicePoints: {},
      points: [],
      catalogues: [],
      legend: [],
      showFilters: false
    })

    const dictionaries = reactive({
      points: computed(() => $groupBy(state.points || [], 'points.catalogues')),
      catalogues: computed(() => Object.fromEntries(state.catalogues?.map(c => ([c.sys.id, c.name])) || []))
    })


    const ordersByDay = computed(() => {
      return (state.points || [])
        .map(point => ({ group: 'Orders', key: format.date(point.timestamp, DATE_FORMAT), value: point.total }))
    })
    const packingTimesByDay = computed(() => {
      return (state.points || [])
        .map(point => ({ group: 'Packing times', key: format.date(point.timestamp, DATE_FORMAT), value: point.points.packing_times }))
    })
    const pickingTimesByDay = computed(() => {
      return (state.points || [])
        .map(point => ({ group: 'Picking times', key: format.date(point.timestamp, DATE_FORMAT), value: point.points.picking_times }))
    })
    const ordersByCatalogueDay = computed(() => {
      return Object.keys(dictionaries.points || [])
        .map(group =>
          dictionaries.points[group].map(point => ({ group, key: format.date(point.timestamp, DATE_FORMAT), value: point.points.ordersByCatalogue[Object.keys(dictionaries.catalogues).find(key => dictionaries.catalogues[key] === group)|| group]})))
          .flat(1)
    })

    const productAvgPerOrdersByDay = computed<Array<{ group: string, value: number, tags?: Array<string>, key: string }>>(() => {
      // APi provides overal product-quantity mean but not by catalogue,
      // so we'll calculate the product total quantity average here using [Pmi=Pci/Gci] (where Pmi = group product mean, Gci = Group order count, Pci = Group product quantity)
      const averageByCatalogue = Object.keys(dictionaries.points || [])
        .map(group => {
          const id = Object.entries(dictionaries.catalogues).find(e => e[1] === group)?.[0]
          return dictionaries.points[group].map(point => ({ group, key: format.date(point.timestamp, DATE_FORMAT), value: !point.points.orders ? 0 : (point.points.catalogue_products[id as string] || 0) / (point.points.orders || 1) }))
        })
        .flat(1)

      return averageByCatalogue
        .concat([...productQuantityMeanByDay.value].map((point, i) => ({ ...point, group: 'Overal Average' })))
    })

    const valueAvgPerOrdersByDay = computed<Array<{ group: string, value: number, key: string }>>(() => {
      const averageByCatalogue = Object.keys(dictionaries.points || [])
        .map(group =>
          dictionaries.points[group].map(point => ({ group, key: format.date(point.timestamp, DATE_FORMAT), value: parseFloat(point.points.revenue || '0') / (point.points.orders || 1) })))
        .flat(1)

      const genericTotal = [...ordersByDay.value].map((point, i) => {
        const revenue = revenueByDay.value[i]

        return {
          ...point,
          group: 'Overal Average',
          value: revenue.key === point.key
            ? !point.value ? 0 : (revenue.value / (point.value)) as any
            : !point.value ? 0 : ((revenueByDay.value.find(p => p.key === point.key)?.value || 1) / point.value) as any
        }
      })

      return averageByCatalogue.concat(genericTotal)
    })

    const PickingOrdersByDay = computed<Array<{ group: string, value: number, key: string }>>(() => {
      return [...pickingTimesByDay.value].map((point, i) => (point.value || [])?.map((p,i) => ({ ...point, value: (p||0)/60, group: moment(point.key).format('MMM D') })))
        .filter(f => Boolean(f) && f?.length).flat(2)
    })
    const PackingOrdersByDay = computed<Array<{ group: string, value: number, key: string }>>(() => {
      return [...packingTimesByDay.value].map((point, i) => (point.value || [])?.map((p,i) => ({ ...point, value: (p||0), group: moment(point.key).format('MMM D') })))
        .filter(f => Boolean(f) && f?.length).flat(2)
    })

    // const filters = {
    //   legend: data => computed(() => {
    //     return state.legend.length === 0
    //       ? data.filter(point => (!(state.legend.includes(point.group))))
    //       : data.filter(point => ((state.legend.includes(point.group))))
    //   })
    // }

    const productQuantityMeanByDay = computed(() => {
      return (state.points || [])
        .map(point => ({ group: 'Products', key: format.date(point.timestamp, DATE_FORMAT), tags: point.points.popular_products || [], value: point.points.products_quantity_range?.[1] || 0 }))
    })

    const productsByDay = computed(() => {
      return (state.points || [])
        .map(point => ({ group: 'Products', key: format.date(point.timestamp, DATE_FORMAT), value: point.points.products }))
    })

    const revenueByDay = computed(() => {
      return (state.points || [])
        .map(point => ({ group: 'Products', key: format.date(point.timestamp, DATE_FORMAT), value: parseFloat(point.points.revenue || '0') }))
    })


    //---
    const charts = reactive({
      productsAndOrdersByDay: {
        data: computed(() => ordersByDay.value.concat(productsByDay.value)),
        options: {
          ...defaultChartOptions, zoomBar: { top: { enabled: true } }
          // title: 'Orders & Product count by day'
        }

      },
      productAvgPerOrdersByDay: {
        data: productAvgPerOrdersByDay,
        options: {
          ...defaultChartOptions, zoomBar: { top: { enabled: true } }
          // title: 'Average Products per Order'
        },
      },
      valueAvgPerOrdersByDay: {
        data: valueAvgPerOrdersByDay,
        options: {
          ...defaultChartOptions, zoomBar: { top: { enabled: true } },
          axes: {
            left: {
              mapsTo: 'value',
              scaleType: ScaleTypes.LINEAR,
              stacked: false,
              title: 'Value'
            },
            bottom: {
              mapsTo: 'key',
              scaleType: 'time',
              title: 'Date'
            },
          }
        },
      },
      PickingOrdersByDay: {
        data: PickingOrdersByDay,
        options: {
          ...defaultChartOptions,
          axes: {
            bottom: {
              scaleType: 'labels',
              mapsTo: 'group',
              title: 'Date'

            },
            left: {
              mapsTo: 'value',
              scaleType: ScaleTypes.LINEAR,
              title: 'Picking Time (Minutes)'
            }
          },
          boxplot: {
            groupMapsTo: 'group'
          },
          timeScale: {
            addSpaceOnEdges: 0.1
          }
        },
      },
      PackingOrdersByDay: {
        data: PackingOrdersByDay,
        options: {
          ...defaultChartOptions,
          axes: {
            bottom: {
              scaleType: 'labels',
              mapsTo: 'group',
              title: 'Date'

            },
            left: {
              mapsTo: 'value',
              scaleType: ScaleTypes.LINEAR,
              title: 'Packing Time (Seconds)'
            }
          },
          boxplot: {
            groupMapsTo: 'group'
          },
          timeScale: {
            addSpaceOnEdges: 0.1
          }
        },
      },
      ordersByDay: {
        data: ordersByDay,
        options: {...defaultChartOptions, zoomBar: { top: { enabled: true } } }
      },
      ordersByCatalogueDay: {
        data: ordersByCatalogueDay,
        options: {...defaultChartOptions, zoomBar: { top: { enabled: true } } }
      },
      // productsByDay: {
      //   data: productsByDay,
      //   options: {
      //     title: 'Products by day',
      //     axes: Mock.DEFAULT_AXES,
      //     height: '300px'
      //   }
      // },
    })


    function toggleFilter (key, i) {
      charts.ordersByCatalogueDay = { data: ordersByCatalogueDay.value, options: charts.ordersByCatalogueDay.options }
      charts.productAvgPerOrdersByDay = { data: productAvgPerOrdersByDay.value, options: charts.ordersByCatalogueDay.options }
      charts.valueAvgPerOrdersByDay = { data: valueAvgPerOrdersByDay.value, options: charts.valueAvgPerOrdersByDay.options }

      // Filter the ordersByCatalogueDay data based on the selected items
      const selectedOrdersData = selectedItems.value.flatMap(item => {
        return charts.ordersByCatalogueDay.data?.filter(point => point.group === item)
      })

      //Filter the productAvgPerOrdersByDay data based on the selected items
      const selectedItemsDataForAvgOrders = selectedItems.value.flatMap(item => {
        return charts.productAvgPerOrdersByDay.data.filter(point => point.group === item)
      })

      // Update the ordersByCatalogueDay data with the filtered data
      charts.ordersByCatalogueDay = { data: selectedOrdersData, options: charts.ordersByCatalogueDay.options }

      //Update the productAvgPerOrdersByDay data with the filtered data
      charts.productAvgPerOrdersByDay = { data: selectedItemsDataForAvgOrders, options: charts.productAvgPerOrdersByDay.options }
    }



    async function fetchPoints () {
      if (!state.timerange) return
      try {
        loading.value = true
        const insights = await $directr.insight.get('order', state.timerange, state.resolution)

        return insights
      } catch (error) {
        $q.notify({ message: 'There was an issue loading insights', type: 'negative', caption: errorToReadableString(error), classes: 'custom-notify error' })
      } finally {
        loading.value = false
      }
    }

    const debouncedFetch = debounce(() => {
      void fetchPoints()
      void fetchServiceInsights()
    }, 2000)

    async function fetchServiceInsights () {
      if (!state.timerange || !state.services?.length) return []

      const services = state.services.filter(service => INSIGHT_SERVICES.includes(service.type))
      const settled = await Promise.allSettled(services
        .map(service => {
          return $sys.insight.get(service.id, state.timerange, state.resolution)
        })
      )

      const fulfilled = settled.filter(f => f.status === 'fulfilled').map(f => (f as PromiseFulfilledResult<any>).value)
      const rejected = settled.filter(f => f.status === 'rejected').map(f => (f as PromiseRejectedResult).reason)

      if (rejected?.length) console.error('Some service insights could not be fetched', rejected)

      return fulfilled
    }


    onMounted(async () => {
      // emit('timerange', state.timerange)
      const catalogues = await $directr.get('catalogue', [{ select: ['id', 'name'] }])
      state.services = await $sys.get('/service')
      state.catalogues = catalogues.items || []
      try {
        const points = await fetchPoints()
        state.points = points?.items?.map(p => {
          p.points.ordersByCatalogue = p.points.catalogues
          p.points.catalogues = Object.keys(p.points.catalogues || {}).map(id => (catalogues.items || []).find(c => c.sys.id === id)?.name || id)
          return p
        })
      } catch (e) {
        console.error(e)
      }
      try {
        const servicePoints = await fetchServiceInsights()
        state.servicePoints = Object.fromEntries(servicePoints.map(res => {
          const key:string = state.services?.find(s => s.id === res.sys.service.id)?.name || res.sys.service.id

          return [key.startsWith(res.sys.service.type) ? key.slice(res.sys.service.type.length) : key, {id: res.sys.service.id, type: res.sys.service.type, items: res.items}]
        }))
      } catch (e) {
        console.error(e)
      }
    })

    // lol bootleg
    const shopifyToArea = computed(() => {
      const keys = Object.keys(state.servicePoints || {})
        .filter(f => state.servicePoints?.[f].type === 'shopify')
      return Object.fromEntries(keys
        .map(key => {
          const points = state.servicePoints?.[key]?.items || []
          return [
            key,
            points.flatMap(point => {
              return Object.keys(point.points).map(group => {
                return { group, key: format.date(point.timestamp, DATE_FORMAT), value: point.points.products || 0 }
              })
            })
          ]
        }))
    })

    const proxyTimeRange = computed({
      get: () => ({ from: state.timerange.start, to: state.timerange.end }),
      set: (value) => {
        state.timerange.start = moment(value.from).startOf('day').toISOString(true)
        state.timerange.end = moment(value.to).endOf('day').toISOString(true)
        void debouncedFetch()
      }
    })

    function showInsightFilters () {
      state.showFilters = true
    }

    function formatDate (d) {
      return date.formatDate(d, 'DD-MM-YYYY')
    }

    const totalOrders = computed(() => ordersByDay.value.reduce((sum, next) => sum + next.value, 0))
    return {
      ...toRefs(state),
      loading,
      shopifyToArea,
      showInsightFilters,
      proxyTimeRange,
      format,
      formatDate,
      dictionaries,
      minmax,
      toggleFilter,
      charts,
      count: {
        orders: totalOrders,
        issues: computed(() => 0),
        product_average: computed(() => productAvgPerOrdersByDay.value.reduce((sum, point) => sum + (point.value || 0), 0) / productAvgPerOrdersByDay.value.length),
        revenue: computed(() => (state.points || []).reduce((sum, next) => sum + parseInt(next.points.revenue || '0'), 0))
      },
      selectedItems,
    }
  }
})
